// ============================================================
// Response shapes for the v2 operations APIs the dashboard drives:
// error groups (docs/v2-partner-api.md §3), proof pack
// (docs/revenue-reconciliation.md, docs/privacy-evidence.md),
// warehouse exports (docs/warehouse-exports.md) and management keys.
// ============================================================

// ---------- error groups ----------

export const ERROR_GROUP_STATUSES = ['new', 'open', 'regressed', 'resolved', 'ignored'] as const
export type ErrorGroupStatus = (typeof ERROR_GROUP_STATUSES)[number]

export interface ErrorGroup {
    id: string
    fingerprint: string
    sub_tenant: string | null
    title: string
    error_type: string
    status: ErrorGroupStatus
    assignee: string | null
    count: number
    first_seen: string
    last_seen: string
    first_release: string | null
    last_release: string | null
    last_path: string | null
    frames: string[]
    resolved_at: string | null
    resolved_in_release: string | null
    regressed_at: string | null
}

/** The title usually starts with the error type already ("TypeError: …"); don't repeat it. */
export function groupLabel(g: Pick<ErrorGroup, 'error_type' | 'title'>): string {
    return !g.error_type || g.title.startsWith(g.error_type)
        ? g.title
        : `${g.error_type}: ${g.title}`
}

export interface ErrorGroupList {
    groups: ErrorGroup[]
    total: number
    limit: number
    offset: number
}

export interface ErrorGroupDetail {
    group: ErrorGroup
    samples: Record<string, unknown>[]
    samples_error?: string
}

export interface ErrorGroupQuery {
    projectId: string
    subTenant?: string | null
    status?: ErrorGroupStatus[]
    q?: string
    sort?: 'last_seen' | 'first_seen' | 'count'
    limit?: number
    offset?: number
}

// ---------- revenue reconciliation ----------

export const ORDER_PRESETS = ['generic', 'shopify', 'stripe'] as const
export type OrderPreset = (typeof ORDER_PRESETS)[number]

export interface ReconRefunds {
    orders: number
    orders_value: number
    yom_refunds: number
    yom_value: number
    matched: number
    missing_in_yom: number
    value_mismatch: number
    extra_in_yom: number
}

export interface ReconSummary {
    orders: number
    orders_value: number
    yom_purchases: number
    yom_value: number
    matched: number
    matched_value_ok: number
    missing_in_yom: number
    missing_value: number
    extra_in_yom: number
    extra_value: number
    value_mismatch: number
    duplicates: number
    duplicate_value: number
    status_conflict: number
    missing_transaction_id: number
    currency_mismatch: number
    coverage: number | null
    revenue_delta: number
    revenue_delta_pct: number | null
    refunds: ReconRefunds
}

export interface ReconDiscrepancy {
    type: string
    sub_tenant: string | null
    order_id: string
    order_created_at: string | null
    order_status: string | null
    order_value: number | null
    order_currency: string | null
    yom_events: number
    yom_value: number | null
    delta: number | null
    event_ids: string
}

export interface ReconciliationReport {
    range: { from: string; to: string; tz: string }
    sub_tenant: string | null
    currency: string
    tolerance: number
    exact: boolean
    orders_supplied: {
        total: number
        first_created_at: string | null
        last_created_at: string | null
        last_updated_at: string | null
    }
    warnings: string[]
    summary: ReconSummary
    by_sub_tenant: Array<ReconSummary & { sub_tenant: string }>
    discrepancies_total: number
    discrepancies: ReconDiscrepancy[]
}

export interface OrdersUploadResult {
    rows: number
    accepted: number
    rejected: number
    errors: Array<{ row: number; error: string }>
}

export interface ReconSource {
    id: string
    name: string
    preset: OrderPreset
    sub_tenant: string | null
    url: string
    created_at: string
    last_received_at: string | null
    /** Only on create, when YOM generated the signing secret. */
    secret?: string
}

// ---------- privacy evidence ----------

export interface PrivacyCounts {
    accepted: number
    server_events: number
    consent: { granted: number; denied: number; unknown: number }
    signals: { gpc: number; dnt: number }
    opt_outs: { requests: number; honored: number; not_honored: number }
    actions: {
        cookieless: number
        dropped: { dnt: number; consent_required: number; total: number }
        pii_fields_scrubbed: number
        events_with_pii_scrubbed: number
        forwarding_suppressed: number
        ad_platform_suppressed: number
        ad_platform_suppressed_gpc: number
    }
    unflagged: number
}

export interface PrivacyEvidenceReport {
    range: { from: string; to: string; tz: string }
    sub_tenant: string | null
    /** Part of the range came from sampled Analytics Engine data. */
    sampled: boolean
    /** First UTC day served from the exact daily counters (null: none in range). */
    exact_from?: string | null
    policy: { statements: string[] } & Record<string, unknown>
    totals: PrivacyCounts
    by_day: Array<PrivacyCounts & { day: string }>
    by_sub_tenant: Array<PrivacyCounts & { sub_tenant: string }>
}

// ---------- warehouse exports ----------

export type ExportType = 's3' | 'bigquery'

export interface WarehouseExport {
    id: string
    name: string
    type: ExportType
    format: 'ndjson.gz' | 'parquet'
    schedule: 'hourly' | 'daily'
    sub_tenant: string | null
    include_internal: boolean
    s3?: {
        endpoint: string
        region: string
        bucket: string
        prefix: string
        path_style: boolean
        access_key_hint: string
    }
    bigquery?: {
        project_id: string
        dataset: string
        table: string
        location: string
        client_email: string
    }
    active: boolean
    disabled_reason: string | null
    status: string
    last_error: string | null
    start_hour: string
    exported_through: string | null
    pending_jobs: number
    retry_hours: string[]
    failed_hours: string[]
    last_run_at: number | string | null
    last_success_at: number | string | null
    created_at: string
    updated_at: string
}

export interface ExportRun {
    id: string
    trigger: string
    status: string
    from_hour: string | null
    to_hour: string | null
    hours: number
    rows: number
    bytes: number
    files: string[]
    pending_jobs: number
    error: string | null
    started_at: number | string
    finished_at: number | string | null
}

export interface ExportTestResult {
    ok: boolean
    error: string | null
    detail: string
}

export interface ExportRunResult {
    status: 'ok' | 'partial' | 'error' | 'busy' | 'noop'
    hours: number
    rows: number
    error?: string | null
}

// ---------- management keys ----------

export const MANAGEMENT_SCOPES = [
    'projects:read',
    'projects:write',
    'keys:write',
    'analytics:delegate',
] as const
export type ManagementScope = (typeof MANAGEMENT_SCOPES)[number]

/** `yom_mk_…` keys belong to the organization, not a project. */
export function isManagementKey(keyPrefix: string): boolean {
    return keyPrefix.startsWith('yom_mk_')
}

export interface ManagementKey {
    id: string
    name: string
    key_prefix: string
    scopes: ManagementScope[]
    expires_at: string | null
    created_at: string
    revoked_at: string | null
    last_used_at: string | null
}

export interface ManagementKeyCreated {
    id: string
    key: string
    name: string
    scopes: ManagementScope[]
    expires_at: string | null
}

// ---------- project keys (v2 /projects/:id/keys) ----------

/** Scopes a v2 project key can carry. Only `ingest` → publishable `pk`; anything else → `sk`. */
export const PROJECT_KEY_SCOPES = ['ingest', 'server', 'read', 'write'] as const
export type ProjectKeyScope = (typeof PROJECT_KEY_SCOPES)[number]

export interface ProjectKey {
    id: string
    name: string
    key_prefix: string
    scopes: string[]
    /** Bound to one sub-tenant (platform customer); null = the whole project. */
    sub_tenant: string | null
    rate_limit: number
    expires_at: string | null
    created_at: string
    revoked_at: string | null
    last_used_at: string | null
}

export interface ProjectKeyCreated {
    id: string
    key: string
    type: 'pk' | 'sk'
    name: string
    scopes: string[]
    sub_tenant: string | null
    hmac: boolean
    expires_at: string | null
}

/** A key made on the old v1 screen: it carries a scope v2 doesn't mint (e.g. `admin`). */
export function isLegacyProjectKey(k: Pick<ProjectKey, 'scopes'>): boolean {
    return k.scopes.some(s => !(PROJECT_KEY_SCOPES as readonly string[]).includes(s))
}

export function projectKeyType(scopes: readonly string[]): 'pk' | 'sk' {
    return scopes.length > 0 && scopes.every(s => s === 'ingest') ? 'pk' : 'sk'
}

// ---------- ad-platform destinations ----------

export type AdDestinationType = 'meta_capi' | 'ga4_mp'

/** The non-secret config GET returns for an ad destination (credential is write-only). */
export interface AdDestinationConfig {
    pixel_id?: string
    measurement_id?: string
    test_event_code?: string
    event_map: Record<string, string>
}

// ---------- data lifecycle (data-subject requests, archive export) ----------

export type PrivacySubjectType = 'visitor' | 'user' | 'email_hash'

/** GET /v2/projects/:id/privacy/requests[/:id] (lifecycle/subjects.ts). */
export interface PrivacySubjectRequest {
    id: string
    kind: 'access' | 'erasure'
    subject_type: PrivacySubjectType
    subject_sha256: string
    status: 'queued' | 'running' | 'completed' | 'failed'
    counts: Record<string, number>
    export_available: boolean
    requested_by: string
    created_at: string
    completed_at: string | null
    expires_at: string | null
    error: string | null
}

/** GET /reports/privacy-evidence/events: recent events an opt-out or privacy action applied to. */
export interface PrivacyEventRow {
    at: string
    event: string
    path: string
    sub_tenant: string
    source: 'client' | 'server'
    consent: string
    gpc: boolean
    dnt: boolean
    cookieless: boolean
    pii_fields_scrubbed: number
    forwarding_suppressed: boolean
    dropped: number
    reasons: string[]
}

export interface PrivacyEventSample {
    sampled: boolean
    events: PrivacyEventRow[]
}

/** One page of GET /archive/export (NDJSON body + the next page's cursor). */
export interface ArchivePage {
    text: string
    files: number
    next: string | null
}
